/**
 * POS Customer Management Page
 * ============================
 * 
 * Manage customers, view purchase history, and track loyalty points.
 */

import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Users,
  Plus,
  Search,
  Pencil,
  Trash2,
  ArrowLeft,
  User,
  Mail,
  Phone,
  MapPin,
  Star,
  ShoppingBag,
  TrendingUp,
  Gift,
  History,
  Tag,
  Loader2,
  ChevronRight,
} from "lucide-react";
import { Link } from "react-router-dom";

const POSCustomersPage = () => {
  const { token } = useAuth();
  
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState("");
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Detail modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [customerHistory, setCustomerHistory] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    postcode: "",
    tags: [],
    notes: "",
    opt_in_marketing: false,
  });
  
  // New tag input
  const [newTag, setNewTag] = useState("");
  
  useEffect(() => {
    fetchCustomers();
    fetchTags();
  }, [selectedTag]);
  
  const fetchCustomers = async () => {
    try {
      let url = `${API}/pos/customers?limit=100`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (selectedTag) url += `&tag=${encodeURIComponent(selectedTag)}`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setCustomers(data);
      }
    } catch (err) {
      console.error("Error fetching customers:", err);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchTags = async () => {
    try {
      const res = await fetch(`${API}/pos/customers/tags`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTags(data);
      }
    } catch (err) {
      console.error("Error fetching tags:", err);
    }
  };
  
  const openAddModal = () => {
    setEditingCustomer(null);
    setFormData({
      name: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      postcode: "",
      tags: [],
      notes: "",
      opt_in_marketing: false,
    });
    setShowModal(true);
  };
  
  const openEditModal = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name || "",
      email: customer.email || "",
      phone: customer.phone || "",
      address: customer.address || "",
      city: customer.city || "",
      postcode: customer.postcode || "",
      tags: customer.tags || [],
      notes: customer.notes || "",
      opt_in_marketing: customer.opt_in_marketing || false,
    });
    setShowModal(true);
  };
  
  const handleSave = async () => {
    if (!formData.name) {
      toast.error("Name is required");
      return;
    }
    
    setSaving(true);
    
    try {
      const url = editingCustomer 
        ? `${API}/pos/customers/${editingCustomer.customer_id}`
        : `${API}/pos/customers`;
      
      const res = await fetch(url, {
        method: editingCustomer ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to save customer");
      }
      
      toast.success(editingCustomer ? "Customer updated" : "Customer created");
      setShowModal(false);
      fetchCustomers();
      fetchTags();
    } catch (err) {
      console.error("Save error:", err);
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };
  
  const handleDelete = async (customerId) => {
    if (!confirm("Are you sure you want to delete this customer?")) return;
    
    try {
      const res = await fetch(`${API}/pos/customers/${customerId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!res.ok) throw new Error("Failed to delete");
      
      toast.success("Customer deleted");
      fetchCustomers();
    } catch (err) {
      toast.error("Failed to delete customer");
    }
  };
  
  const viewCustomerDetail = async (customer) => {
    setCustomerDetail(customer);
    setShowDetailModal(true);
    
    // Fetch purchase history
    try {
      const res = await fetch(`${API}/pos/customers/${customer.customer_id}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setCustomerHistory(data);
      }
    } catch (err) {
      console.error("Error fetching history:", err);
    }
  };
  
  const addTagToForm = () => {
    if (newTag && !formData.tags.includes(newTag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag]
      }));
      setNewTag("");
    }
  };
  
  const removeTagFromForm = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };
  
  const formatPrice = (pence) => `£${(pence / 100).toFixed(2)}`;
  
  const handleSearch = (e) => {
    e.preventDefault();
    fetchCustomers();
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-gray-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header - Brand Gradient */}
        <div className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 rounded-xl shadow-lg p-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/merchant/pos" className="text-white/80 hover:text-white">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2 text-white">
                  <Users className="h-6 w-6 text-fuchsia-200" />
                  Customer Management
                </h1>
                <p className="text-indigo-100 text-sm mt-1">
                  {customers.length} customers
                </p>
              </div>
            </div>
            <Button onClick={openAddModal} className="bg-white text-indigo-700 hover:bg-indigo-50" data-testid="add-customer-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Customer
            </Button>
          </div>
        </div>
        
        {/* Search and Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <form onSubmit={handleSearch} className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name, email, or phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-white border-gray-200"
                data-testid="customer-search"
              />
            </div>
          </form>
          
          {/* Tag filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant={selectedTag === "" ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedTag("")}
              className={selectedTag === "" ? "bg-indigo-600 text-white" : "border-gray-200"}
            >
              All
            </Button>
            {tags.slice(0, 5).map(t => (
              <Button
                key={t.tag}
                variant={selectedTag === t.tag ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedTag(t.tag)}
                className={selectedTag === t.tag ? "bg-indigo-600 text-white" : "border-gray-200"}
              >
                {t.tag} ({t.count})
              </Button>
            ))}
          </div>
        </div>
        
        {/* Customer List */}
        {customers.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200 shadow-sm">
            <Users className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">No customers found</p>
            <Button onClick={openAddModal} className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white">
              <Plus className="h-4 w-4 mr-2" />
              Add First Customer
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {customers.map(customer => (
              <div
                key={customer.customer_id}
                className="bg-white/50 rounded-xl border border-gray-200 p-4 hover:border-indigo-500/30 transition-colors"
                data-testid={`customer-${customer.customer_id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-indigo-500/20 flex items-center justify-center">
                      <User className="h-6 w-6 text-indigo-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{customer.name}</h3>
                      <div className="flex items-center gap-3 text-sm text-gray-400 mt-1">
                        {customer.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {customer.email}
                          </span>
                        )}
                        {customer.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {customer.phone}
                          </span>
                        )}
                      </div>
                      {customer.tags?.length > 0 && (
                        <div className="flex items-center gap-1 mt-2">
                          {customer.tags.slice(0, 3).map(tag => (
                            <span key={tag} className="text-xs bg-gray-100 text-slate-300 px-2 py-0.5 rounded">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6 text-sm">
                    <div className="text-center">
                      <p className="text-gray-400">Orders</p>
                      <p className="font-semibold text-lg">{customer.total_orders}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-gray-400">Spent</p>
                      <p className="font-semibold text-lg text-emerald-400">{formatPrice(customer.total_spent)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-gray-400">Points</p>
                      <p className="font-semibold text-lg text-amber-400 flex items-center gap-1">
                        <Star className="h-4 w-4" />
                        {customer.loyalty_points}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => viewCustomerDetail(customer)}
                      className="border-gray-200"
                    >
                      View
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditModal(customer)}
                      className="text-gray-400 hover:text-indigo-400"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(customer.customer_id)}
                      className="text-gray-400 hover:text-red-400"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-white border-gray-200 max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCustomer ? "Edit Customer" : "Add Customer"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Smith"
                className="bg-gray-100 border-gray-200"
                data-testid="customer-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@email.com"
                  className="bg-gray-100 border-gray-200"
                  data-testid="customer-email-input"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+44 7700 900000"
                  className="bg-gray-100 border-gray-200"
                  data-testid="customer-phone-input"
                />
              </div>
            </div>
            
            <div>
              <Label>Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 High Street"
                className="bg-gray-100 border-gray-200"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>City</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="London"
                  className="bg-gray-100 border-gray-200"
                />
              </div>
              <div>
                <Label>Postcode</Label>
                <Input
                  value={formData.postcode}
                  onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                  placeholder="SW1A 1AA"
                  className="bg-gray-100 border-gray-200"
                />
              </div>
            </div>
            
            <div>
              <Label>Tags</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Add tag..."
                  className="bg-gray-100 border-gray-200"
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTagToForm())}
                />
                <Button type="button" onClick={addTagToForm} variant="outline" className="border-gray-200">
                  Add
                </Button>
              </div>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {formData.tags.map(tag => (
                    <span
                      key={tag}
                      className="text-xs bg-indigo-500/20 text-indigo-300 px-2 py-1 rounded flex items-center gap-1"
                    >
                      {tag}
                      <button type="button" onClick={() => removeTagFromForm(tag)} className="hover:text-white">×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            
            <div>
              <Label>Notes</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any notes about this customer..."
                className="bg-gray-100 border-gray-200"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="optIn"
                checked={formData.opt_in_marketing}
                onChange={(e) => setFormData({ ...formData, opt_in_marketing: e.target.checked })}
                className="rounded"
              />
              <label htmlFor="optIn" className="text-sm text-gray-400">
                Opt-in to marketing communications
              </label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)} className="border-gray-200">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-customer-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingCustomer ? "Save Changes" : "Add Customer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Customer Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="bg-white border-gray-200 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-indigo-400" />
              {customerDetail?.name}
            </DialogTitle>
          </DialogHeader>
          
          {customerDetail && (
            <div className="space-y-6">
              {/* Customer Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-gray-100/50 rounded-lg p-4 text-center">
                  <ShoppingBag className="h-5 w-5 mx-auto text-gray-400 mb-1" />
                  <p className="text-2xl font-bold">{customerHistory?.stats?.total_orders || 0}</p>
                  <p className="text-xs text-gray-400">Orders</p>
                </div>
                <div className="bg-gray-100/50 rounded-lg p-4 text-center">
                  <TrendingUp className="h-5 w-5 mx-auto text-emerald-400 mb-1" />
                  <p className="text-2xl font-bold text-emerald-400">{formatPrice(customerHistory?.stats?.total_spent || 0)}</p>
                  <p className="text-xs text-gray-400">Total Spent</p>
                </div>
                <div className="bg-gray-100/50 rounded-lg p-4 text-center">
                  <ShoppingBag className="h-5 w-5 mx-auto text-blue-400 mb-1" />
                  <p className="text-2xl font-bold text-blue-400">{formatPrice(customerHistory?.stats?.average_basket || 0)}</p>
                  <p className="text-xs text-gray-400">Avg Basket</p>
                </div>
                <div className="bg-gray-100/50 rounded-lg p-4 text-center">
                  <Star className="h-5 w-5 mx-auto text-amber-400 mb-1" />
                  <p className="text-2xl font-bold text-amber-400">{customerHistory?.stats?.loyalty_points || 0}</p>
                  <p className="text-xs text-gray-400">Points</p>
                </div>
              </div>
              
              {/* Contact Info */}
              <div className="bg-gray-100/30 rounded-lg p-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <User className="h-4 w-4 text-gray-400" />
                  Contact Info
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {customerDetail.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <span>{customerDetail.email}</span>
                    </div>
                  )}
                  {customerDetail.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-gray-400" />
                      <span>{customerDetail.phone}</span>
                    </div>
                  )}
                  {(customerDetail.address || customerDetail.city) && (
                    <div className="flex items-center gap-2 col-span-2">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span>
                        {[customerDetail.address, customerDetail.city, customerDetail.postcode]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Top Products */}
              {customerHistory?.top_products?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Gift className="h-4 w-4 text-gray-400" />
                    Top Products
                  </h4>
                  <div className="space-y-2">
                    {customerHistory.top_products.map((prod, idx) => (
                      <div key={idx} className="flex justify-between items-center bg-gray-100/30 rounded p-2 text-sm">
                        <span>{prod.name}</span>
                        <div className="flex items-center gap-4">
                          <span className="text-gray-400">{prod.quantity} purchased</span>
                          <span className="text-emerald-400">{formatPrice(prod.revenue)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Recent Transactions */}
              {customerHistory?.transactions?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <History className="h-4 w-4 text-gray-400" />
                    Recent Transactions
                  </h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {customerHistory.transactions.slice(0, 10).map((txn) => (
                      <div key={txn.transaction_id} className="flex justify-between items-center bg-gray-100/30 rounded p-3 text-sm">
                        <div>
                          <p className="font-medium">{txn.transaction_number}</p>
                          <p className="text-xs text-gray-400">
                            {new Date(txn.completed_at).toLocaleDateString()} - {txn.totals?.items_count} items
                          </p>
                        </div>
                        <span className="font-semibold text-emerald-400">
                          {formatPrice(txn.totals?.total || 0)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Loyalty History */}
              {customerHistory?.loyalty_history?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Star className="h-4 w-4 text-amber-400" />
                    Points History
                  </h4>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {customerHistory.loyalty_history.slice(0, 5).map((lty) => (
                      <div key={lty.loyalty_txn_id} className="flex justify-between items-center text-sm">
                        <span className="text-gray-400">{lty.reason}</span>
                        <span className={lty.type === "earn" ? "text-emerald-400" : "text-red-400"}>
                          {lty.type === "earn" ? "+" : ""}{lty.points} pts
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default POSCustomersPage;
